import React, { useEffect, useRef, useState } from 'react';
import mapboxgl, { GeoJSONSource } from 'mapbox-gl';
import './RoutingDebugger.scss';
import 'mapbox-gl/dist/mapbox-gl.css';
import polyline from '@mapbox/polyline';
import * as GeoJSON from 'geojson';
import { rangeConvert } from './colorUtils';
import convert from 'color-convert';
import { Vehicle, VehicleRoutingSolution } from './VehicleRoutingSolution';
import * as osrm from './osrm';
import { zipWithNext } from './utils';
import { RoutingResponse } from './RoutingResponse';
import {
  getUpdatedSolution,
  shortWindowString,
} from './vehicleRoutingSolutionUtils';
import { EditedVehicle } from './generalTypes';
import { settings } from '../../settings';
import { getAuthHeadersFromCookie } from '../../lib/AuthClient/cookie';
import { Link, useHistory, useParams } from 'react-router-dom';
import { mapboxApiKey } from '../../fetch/mapboxApi';

mapboxgl.accessToken = mapboxApiKey;

const borderLayer: any = {
  type: 'line',
  id: 'lines',
  source: 'directions',
  layout: {},
  paint: {
    'line-color': 'black',
    'line-width': 1,
  },
};

function getSolutionScore(solutionFile: VehicleRoutingSolution) {
  return fetch(`${settings.autobundlerUrl}/api/debug/solution/score`, {
    headers: {
      Authorization: getAuthHeadersFromCookie()?.Authorization,
    },
    method: 'post',
    body: JSON.stringify(solutionFile),
  })
    .then(res => res.text())
    .then(res => {
      if (res.startsWith('"')) {
        return JSON.parse(res);
      } else {
        return res;
      }
    });
}

function drawDirections(map: mapboxgl.Map, features: GeoJSON.Feature[]) {
  const source = map.getSource(`directions`) as GeoJSONSource;
  source.setData({
    type: 'FeatureCollection',
    features: features,
  });
}

enum HighlightOption {
  EarlyWindow = 'EarlyWindow',
  LateWindow = 'LateWindow',
  FourHourWindow = 'FourHourWindow',
  Unpreferred = 'Unpreferred',
}

export function RoutingDebugger() {
  const params = useParams<{ partition?: string }>();
  const history = useHistory();

  const mapContainer = useRef(null);
  const map = useRef<mapboxgl.Map>(null);
  const [mapReady, setMapReady] = useState(false);
  const [markers, setMarkers] = useState<mapboxgl.Marker[]>([]);
  const [solutionFile, setSolutionFile] = useState<VehicleRoutingSolution>();
  const [solutionFileName, setSolutionFileName] = useState('');
  const [initialScore, setInitialScore] = useState('');
  const [editedScore, setEditedScore] = useState('');
  const [editedVehicles, setEditedVehicles] = useState<EditedVehicle[]>([]);
  const [objectUrl, setObjectUrl] = useState<string>();
  const [scores, setScores] = useState<Map<number, number>>(new Map());
  const [scoreInfos, setScoreInfos] = useState<Map<number, string>>(new Map());
  const [solutionId, setSolutionId] = useState(params.partition || '');
  const [isFetchingSolution, setIsFetchingSolution] = useState(false);
  const [highlighting, setHighlighting] = useState<
    Record<HighlightOption, boolean>
  >({
    [HighlightOption.EarlyWindow]: false,
    [HighlightOption.LateWindow]: false,
    [HighlightOption.FourHourWindow]: false,
    [HighlightOption.Unpreferred]: false,
  });

  const onSolutionIdChange = event => {
    setSolutionId(event.target.value);
  };

  const onFetchSolution = () => {
    setIsFetchingSolution(true);
    fetch(`${settings.autobundlerUrl}/api/debug/solution/file/${solutionId}`, {
      headers: {
        Authorization: getAuthHeadersFromCookie()?.Authorization,
      },
      method: 'post',
      body: JSON.stringify(solutionFile),
    })
      .then(res => {
        if (!res.ok) {
          throw new Error(
            `Request failed with status ${res.status} ${res.statusText}`
          );
        }
        return res.json();
      })
      .then(res => {
        history.replace(`/planner/routing-debugger/${solutionId}`);
        setSolutionFile(res);
        setSolutionFileName(`${solutionId}.json`);
      })
      .catch(err => {
        alert(`Fetch of solution failed: ${err}`);
      })
      .finally(() => {
        setIsFetchingSolution(false);
      });
  };

  useEffect(() => {
    if (map.current) return; // initialize map only once
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v11',
      // If using maplibre instead of mapbox:
      // style: 'https://api.maptiler.com/maps/streets/style.json?key=<key>',
      hash: true,
      center: [10.726, 59.9868],
      zoom: 9.31,
    });
    map.current.on('load', () => {
      map.current.addSource(borderLayer.source, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [],
        },
      });
      map.current.addLayer(borderLayer);
      setMapReady(true);
    });
  });

  useEffect(() => {
    if (!solutionFile) return;
    let updatedSolution: VehicleRoutingSolution;
    try {
      updatedSolution = getUpdatedSolution(solutionFile, editedVehicles);
    } catch (e) {
      return;
    }
    const originalCount = solutionFile.vehicles.flatMap(
      it => it.deliveries
    ).length;
    const updatedCount = updatedSolution.vehicles.flatMap(
      it => it.deliveries
    ).length;
    if (originalCount !== updatedCount) return;

    const jsonString = JSON.stringify(updatedSolution);
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
    }
    const blob = new Blob([jsonString], { type: 'text/plain' });
    setObjectUrl(URL.createObjectURL(blob));
  }, [editedVehicles]);

  useEffect(() => {
    if (!solutionFile) return;
    getSolutionScore(solutionFile).then(res => setInitialScore(res));
  }, [solutionFile]);

  useEffect(() => {
    if (!solutionFile) return;
    const newEditedVehicles: EditedVehicle[] = solutionFile.vehicles.map(
      vehicle => ({
        courierId: vehicle.courierId || vehicle.placeholderId,
        orderIds: vehicle.deliveries
          .map(delivery => delivery.orderId)
          .join('\n'),
      })
    );
    setEditedVehicles(newEditedVehicles);
  }, [solutionFile]);

  const onFileInputChange = event => {
    const file: File = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = fileEvent => {
        const fileText = fileEvent.target.result as string;
        const solution = JSON.parse(fileText) as VehicleRoutingSolution;
        history.replace(`/planner/routing-debugger`);
        setSolutionFile(solution);
        setSolutionFileName(file.name);
      };
      reader.readAsText(file);
    }
  };

  const onVehicleOrderIdsChange = courierId => event => {
    event.persist();
    setEditedVehicles(prevState =>
      prevState.map(v => {
        if (v.courierId !== courierId) {
          return v;
        } else {
          return {
            ...v,
            orderIds: event.target.value,
          };
        }
      })
    );
  };

  function drawVehicleMarkers(updatedVehicles: Vehicle[]) {
    markers.forEach(marker => {
      if (!marker) return;
      marker.remove();
    });
    const newMarkers = updatedVehicles.flatMap(
      (vehicle, i): mapboxgl.Marker[] => {
        const matrix = vehicle.distanceCalculator.matrix.matrix;
        return vehicle.deliveries.map((delivery, deliveryIndex) => {
          const preference = vehicle.preferredZones[delivery.zoneId];
          const noHighlightingSelected = !Object.values(highlighting).some(
            v => v
          );
          const highlight =
            noHighlightingSelected ||
            (highlighting[HighlightOption.EarlyWindow] &&
              shortWindowString(delivery.order.window) === '17:30-19:30') ||
            (highlighting[HighlightOption.LateWindow] &&
              shortWindowString(delivery.order.window) === '19:30-21:30') ||
            (highlighting[HighlightOption.FourHourWindow] &&
              shortWindowString(delivery.order.window) === '17:30-21:30') ||
            (highlighting[HighlightOption.Unpreferred] &&
              preference !== 'LIKE');
          return new mapboxgl.Marker({
            scale: 0.5,
            color: `#${convert.hsl.hex(
              rangeConvert(i, 0, updatedVehicles.length - 1, 80, 360),
              highlight ? 100 : 0,
              highlight ? 50 : 80
            )}`,
          })
            .setPopup(
              new mapboxgl.Popup().setHTML(
                [
                  `Courier: ${vehicle.courierId || vehicle.placeholderId}`,
                  `Delivery index: ${deliveryIndex}`,
                  `Order id: ${delivery.orderId}`,
                  `Window: ${shortWindowString(delivery.order.window)}`,
                  `Preference: ${preference}`,
                ].join('<br>')
              )
            )
            .setLngLat(delivery.order.location)
            .addTo(map.current);
        });
      }
    );
    setMarkers(newMarkers);
  }

  const onDrawLines = () => {
    const updatedVehicles = getUpdatedSolution(
      solutionFile,
      editedVehicles
    ).vehicles;
    drawVehicleMarkers(updatedVehicles);

    const lineFeatures = updatedVehicles.flatMap(
      (vehicle, i): GeoJSON.Feature[] =>
        zipWithNext(
          vehicle.deliveries.map(delivery => delivery.order.location)
        ).map(([from, to]) => {
          return {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'LineString',
              coordinates: [
                [from.lon, from.lat],
                [to.lon, to.lat],
              ],
            },
          };
        })
    );

    drawDirections(map.current, lineFeatures);
  };

  const onDrawActualRoutes = () => {
    const updatedVehicles = getUpdatedSolution(
      solutionFile,
      editedVehicles
    ).vehicles;
    drawVehicleMarkers(updatedVehicles);

    Promise.all(
      updatedVehicles
        .map(vehicle =>
          vehicle.deliveries.map(delivery => delivery.order.location)
        )
        .map(points => osrm.routeBetweenPoints(points))
    )
      .then((responseArrs: RoutingResponse[][]) =>
        responseArrs.flatMap((osrmRoutingResults): GeoJSON.Feature[] => {
          const geometries = osrmRoutingResults.map(
            res =>
              polyline.toGeoJSON(res.routes[0].geometry) as GeoJSON.Geometry
          );
          return geometries.map(geometry => ({
            type: 'Feature',
            properties: {},
            geometry: geometry,
          }));
        })
      )
      .then(features => {
        drawDirections(map.current, features);
      });
  };

  const onGetUpdatedScore = () => {
    const updatedSolution = getUpdatedSolution(solutionFile, editedVehicles);
    getSolutionScore(updatedSolution).then(res => setEditedScore(res));
  };

  useEffect(() => {
    if (!solutionFile) return;
    let updatedSolution: VehicleRoutingSolution;
    try {
      updatedSolution = getUpdatedSolution(solutionFile, editedVehicles);
    } catch (e) {
      return;
    }
    const originalCount = solutionFile.vehicles.flatMap(
      it => it.deliveries
    ).length;
    const updatedCount = updatedSolution.vehicles.flatMap(
      it => it.deliveries
    ).length;
    if (originalCount !== updatedCount) return;

    const newScores: [number, number][] = updatedSolution.vehicles.map(
      vehicle => {
        const matrix = vehicle.distanceCalculator.matrix.matrix;
        const score = zipWithNext(
          [vehicle.startLocation].concat(
            vehicle.deliveries.map(delivery => delivery.order.location)
          )
        )
          .map(([from, to]) => matrix[from.index][to.index])
          .reduce((prev, curr) => prev + curr, 0);
        return [vehicle.courierId || vehicle.placeholderId, score];
      }
    );
    setScores(new Map(newScores));
    const newScoreInfos: [number, string][] = updatedSolution.vehicles.map(
      vehicle => {
        const likeCount = vehicle.deliveries.filter(
          d => vehicle.preferredZones[d.zoneId] === 'LIKE'
        ).length;
        const dislikeCount = vehicle.deliveries.filter(
          d => vehicle.preferredZones[d.zoneId] === 'DISLIKE'
        ).length;
        const deliveryCount = vehicle.deliveries.length;
        return [
          vehicle.courierId || vehicle.placeholderId,
          `T: ${deliveryCount}/${vehicle.targetParcels}\nP: ${likeCount}/${deliveryCount} (${dislikeCount})`,
        ];
      }
    );
    setScoreInfos(new Map(newScoreInfos));
  }, [editedVehicles]);

  const solutionDate = solutionFile?.vehicles
    ?.find(v => v.deliveries.length > 0)
    ?.deliveries[0]?.order?.window?.start?.substring(0, 10);

  return (
    <div className="routing-debugger">
      <div
        ref={mapContainer}
        style={{ width: '100%', height: 'calc(100vh - 300px)' }}
      />
      <div style={{ width: '100%', height: '300px' }}>
        <div style={{ float: 'left', width: 250 }}>
          <div>
            <input
              type="file"
              accept="application/json"
              onChange={onFileInputChange}
              disabled={isFetchingSolution}
            />
          </div>
          <div>
            <input
              type="text"
              placeholder="<partition-id>"
              size={15}
              value={solutionId}
              onChange={onSolutionIdChange}
            />
            <button onClick={onFetchSolution} disabled={isFetchingSolution}>
              Load solution
            </button>
          </div>
          <p>Loaded: {solutionFileName}</p>
          <p>
            <a
              href={objectUrl}
              download={solutionFileName.replace('.json', '-edited.json')}
            >
              Download solution (with edits)
            </a>
          </p>
          {solutionDate && (
            <p>
              <Link to={`/planner/partitions/${solutionDate}`} target="_blank">
                See partitioning
              </Link>
            </p>
          )}
          <div>
            <button onClick={onDrawLines}>Draw lines</button>
            <button onClick={onDrawActualRoutes}>Draw actual routes</button>
            <button onClick={onGetUpdatedScore}>Get updated score</button>
          </div>
          <div>
            <h4>Highlight:</h4>
            <div>
              {Object.keys(HighlightOption).map(option => (
                <label key={option}>
                  {option}
                  <input
                    type="checkbox"
                    checked={highlighting[option as HighlightOption]}
                    onChange={() =>
                      setHighlighting(prev => ({
                        ...prev,
                        [option]: !prev[option],
                      }))
                    }
                  />
                </label>
              ))}
            </div>
          </div>
        </div>
        <div style={{ float: 'left', width: 250 }}>
          {editedScore && (
            <>
              <h4>Edited score</h4>
              <pre>{editedScore}</pre>
            </>
          )}
          <h4>Initial score (as loaded)</h4>
          <pre>{initialScore}</pre>
        </div>
        {editedVehicles.map(vehicle => (
          <div key={vehicle.courierId} style={{ float: 'left', width: 110 }}>
            <h3>
              <Link
                to={`/planner/preferred-areas/${vehicle.courierId}`}
                target="_blank"
              >
                {vehicle.courierId}
              </Link>
            </h3>
            <p>Len(s): {scores.get(vehicle.courierId)}</p>
            <pre>{scoreInfos.get(vehicle.courierId)}</pre>
            <textarea
              value={vehicle.orderIds}
              onChange={onVehicleOrderIdsChange(vehicle.courierId)}
              rows={vehicle.orderIds.split('\n').length + 1}
              cols={9}
            ></textarea>
          </div>
        ))}
      </div>
    </div>
  );
}
